import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Brand, BrandConfig } from '../shared/models/brand.model';
import { BRAND_CONFIGS } from '../../environments/brand-configs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class BrandService {
    private readonly brandSubject = new BehaviorSubject<Brand>(this.detectFromHostname());

    get brand$(): Observable<Brand> {
        return this.brandSubject.asObservable();
    }

    get current(): Brand {
        return this.brandSubject.value;
    }

    get config(): BrandConfig {
        return BRAND_CONFIGS[this.current];
    }

    setBrand(brand: Brand): void {
        if (brand === this.brandSubject.value) return;
        this.brandSubject.next(brand);
        this.applyTheme(brand);
    }

    init(): void {
        this.applyTheme(this.brandSubject.value);
    }

    private detectFromHostname(): Brand {
        const params = new URLSearchParams(window.location.search);
        if (params.get('brand') === 'carpeak') return 'carpeak';
        if (params.get('brand') === 'autobrief') return 'autobrief';
        return window.location.hostname.includes('carpeak') ? 'carpeak' : 'autobrief';
    }

    // Isolated behind its own method so tests can stub it without touching `environment` itself.
    private resolveFaviconEnv(): string | undefined {
        return (environment as { faviconEnv?: string }).faviconEnv;
    }

    private applyTheme(brand: Brand): void {
        const config = BRAND_CONFIGS[brand];
        const root = document.documentElement;
        root.style.setProperty('--color-primary', config.primaryColor);
        root.style.setProperty('--color-primary-dark', config.primaryDark);
        root.style.setProperty('--color-primary-washed', config.primaryWashed);
        root.style.setProperty('--color-primary-washed-off', config.primaryWashedOff);
        root.style.setProperty('--color-success', config.successColor);
        root.style.setProperty('--color-success-washed', config.successWashed);
        document.title = config.appName;

        const faviconEnv = this.resolveFaviconEnv();
        const dir = faviconEnv ? `assets/favicon/envs/${brand}/${faviconEnv}/` : config.faviconDir;
        this.applyFavicons(dir, !!faviconEnv);
    }

    private applyFavicons(dir: string, envActive: boolean): void {
        const map: Record<string, string> = {
            'apple-touch-icon': `${dir}apple-touch-icon.png`,
            'icon-32': `${dir}favicon-32x32.png`,
            'icon-16': `${dir}favicon-16x16.png`,
        };
        document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="apple-touch-icon"]').forEach(el => {
            const sizes = el.getAttribute('sizes') ?? '';
            if (el.rel === 'apple-touch-icon') el.href = map['apple-touch-icon'];
            else if (sizes === '32x32') el.href = map['icon-32'];
            else if (sizes === '16x16') el.href = map['icon-16'];
        });

        // Production (and docs/CI/locale builds) never reach here — `envActive` is only true
        // when `faviconEnv` is set, which is only true for local/alpha/beta/demo. This is the
        // guarantee that production's <head> is never touched by this new code path.
        if (!envActive) return;

        this.upsertLink('icon', '48x48', `${dir}favicon-48x48.png`, 'image/png');
        this.upsertLink('shortcut icon', null, `${dir}favicon.ico`, 'image/x-icon');
    }

    private upsertLink(rel: string, sizes: string | null, href: string, type: string): void {
        const selector = sizes ? `link[rel="${rel}"][sizes="${sizes}"]` : `link[rel="${rel}"]`;
        let link = document.querySelector<HTMLLinkElement>(selector);
        if (!link) {
            link = document.createElement('link');
            link.rel = rel;
            if (sizes) link.setAttribute('sizes', sizes);
            link.type = type;
            document.head.appendChild(link);
        }
        link.href = href;
    }
}
